/**
 * GuidedTour — interactive sales presentation for the demo environment.
 *
 * Walks a visitor through Crystal Dispatch in a logical order, driving the admin
 * tab navigation as it goes. Each step explains what the feature does, why
 * operators benefit, the problem it solves, and the operational payoff. The
 * visitor can step forward/back, jump out to "Explore freely" at any time, or
 * relaunch the tour. Gated by REACT_APP_DEMO_MODE (rendered only in demo builds).
 */
import React, { useEffect, useState, useCallback } from "react";
import {
  Sparkles, ArrowRight, ArrowLeft, X, Compass, Copy,
  Bot, LayoutDashboard, Map as MapIcon, Users, ShieldCheck, HeartPulse,
  Gauge, Building2, Phone, MessageSquare, Car, UserRound, BarChart3, Award,
} from "lucide-react";
import { toast } from "sonner";

// target = admin tab id to switch to (Admin.jsx switch). commsTab deep-links a
// Communications sub-tab. credentials = role demo login shown for the visitor to
// open in a separate incognito window (role apps can't share the admin session).
const STEPS = [
  {
    icon: Sparkles, title: "Welcome to Crystal Dispatch",
    what: "An end-to-end transport operations platform — dispatch, drivers, compliance, communications, corporate billing and analytics in one place.",
    why: "Operators usually juggle 5+ disconnected tools. Crystal Dispatch replaces that patchwork with a single operating system.",
    payoff: "This short tour shows how each part solves a real operational problem. You can exit and explore freely at any time.",
  },
  {
    icon: LayoutDashboard, title: "Operations Dashboard", target: "dashboard",
    what: "Your control-room home: live job volume, driver availability, revenue and the day's operational snapshot at a glance.",
    why: "Controllers see the whole operation on one screen instead of hunting across systems.",
    problem: "Fragmented visibility leads to slow decisions and missed jobs.",
    payoff: "Faster, better-informed decisions and fewer surprises during a shift.",
  },
  {
    icon: Bot, title: "AI Operations Copilot", target: "copilot",
    what: "On-demand, plain-English AI summaries of operations, ride health, fatigue, compliance, KPIs and corporate performance.",
    why: "Turns raw dashboards into clear 'what needs my attention now' guidance for managers.",
    problem: "Busy operators don't have time to read every chart — risks get buried in the data.",
    payoff: "Surfaces urgent issues (expiring docs, over-limit drivers, stuck rides) instantly, supporting confident decisions.",
  },
  {
    icon: MapIcon, title: "Live Dispatch Control Room", target: "map",
    what: "A real-time map of every active job and driver, with automatic allocation, offer rotation and manual dispatcher override.",
    why: "Dispatchers handle far more concurrent jobs without manual phone-juggling.",
    problem: "Manual allocation caps how many bookings one dispatcher can manage and causes delays.",
    payoff: "More bookings per dispatcher and faster booking-to-pickup times.",
  },
  {
    icon: Users, title: "Fleet & Driver Management", target: "fleet",
    what: "Manage drivers and vehicles — status, profiles, approval state and availability.",
    why: "One source of truth for who can work and what they're driving.",
    problem: "Driver/vehicle records scattered across spreadsheets get out of date.",
    payoff: "Always-current fleet data and the right drivers earning at the right time.",
  },
  {
    icon: ShieldCheck, title: "Compliance Management", target: "compliance",
    what: "Tracks nine driver & vehicle documents (incl. Private Hire Vehicle Licence and V5C) with expiry monitoring and automated reminders.",
    why: "Compliance is enforced at the point of dispatch — non-compliant drivers can't take jobs.",
    problem: "Manual document chasing leads to expired licences slipping through — a major regulatory risk.",
    payoff: "Reduced regulatory and reputational risk, with renewals chased automatically.",
  },
  {
    icon: HeartPulse, title: "Ride Health Monitoring", target: "health",
    what: "Continuously detects stuck rides, offers past their time limit, and recovery activity handled by the automated 'janitor'.",
    why: "Problems are caught — and often auto-corrected — before a customer is let down.",
    problem: "Silent failures (a job nobody picked up) become angry customers and lost revenue.",
    payoff: "Higher reliability and fewer missed jobs, with clear 'needs a human' vs 'auto-handled' separation.",
  },
  {
    icon: Gauge, title: "Driver Fatigue Dashboard", target: "fatigue",
    what: "Monitors each driver's online and driving hours against safety limits, flagging those approaching or over the threshold.",
    why: "Built-in driver safety and duty-of-care, not an afterthought.",
    problem: "Untracked hours create safety risk and potential licensing breaches.",
    payoff: "Safer drivers, demonstrable duty-of-care, and protection against fatigue-related incidents.",
  },
  {
    icon: Building2, title: "Corporate Accounts & Cost Centres", target: "corporate",
    what: "Manage business accounts with cost-centre allocation, monthly statements, credit terms and spend reporting.",
    why: "Turns one-off riders into recurring B2B revenue with proper billing tooling included.",
    problem: "Without account billing, corporate work means manual invoicing and leakage.",
    payoff: "Corporate account growth and clean, defensible monthly billing.",
  },
  {
    icon: Phone, title: "Booking Line (Voice Bookings)", target: "rides",
    what: "Phone bookings taken by a dispatcher flow straight into the same dispatch pipeline — including scheduled rides for later.",
    why: "Captures customers who still prefer to call, with zero double-entry.",
    problem: "Phone bookings logged on paper get lost or mis-keyed.",
    payoff: "Every channel — app and phone — feeds one reliable workflow.",
  },
  {
    icon: MessageSquare, title: "WhatsApp Integration", target: "inbox",
    what: "Customer messaging over WhatsApp (architecture implemented; activation subject to Meta WhatsApp Business approval), with SMS fallback.",
    why: "Meets customers on the channel they already use, with a unified inbox.",
    problem: "Scattered messaging across personal phones isn't auditable or professional.",
    payoff: "Professional, auditable communications and improved customer experience.",
  },
  {
    icon: Car, title: "Driver App",
    what: "A simple mobile workflow for drivers: receive jobs, navigate, manage compliance documents, and communicate via masked numbers.",
    why: "Drivers stay productive and private; controllers get accurate status.",
    problem: "Clunky driver tools cause errors and expose personal phone numbers.",
    payoff: "Smoother driver experience and protected privacy.",
    credentials: { label: "Driver demo login", email: "demo.driver1@crystaldispatch.demo", role: "Driver" },
  },
  {
    icon: UserRound, title: "Passenger App",
    what: "A fast booking experience for customers: request a ride, track the driver, and receive SMS journey notifications.",
    why: "Professional, transparent service that builds trust and repeat bookings.",
    problem: "Opaque, slow booking pushes customers to competitors.",
    payoff: "Better retention and a polished brand experience.",
    credentials: { label: "Passenger demo login", email: "demo.pax1@crystaldispatch.demo", role: "Passenger" },
  },
  {
    icon: BarChart3, title: "KPIs, Revenue & Reporting", target: "dashboard",
    what: "Revenue with a Gross · Refunds · Net breakdown, job volume, driver utilisation and pickup/trip times.",
    why: "Run the business on evidence, not gut feel.",
    problem: "Without clear KPIs, operators can't see what's working or where money leaks.",
    payoff: "Data-driven decisions that protect margin and grow the operation.",
  },
  {
    icon: Award, title: "Why Crystal Dispatch?",
    what: "Real-time dispatch, AI Copilot, compliance enforcement, Ride Health, fatigue controls, voice masking, corporate accounts and a full audit trail — together.",
    why: "Many systems can take a booking; far fewer combine resilience, safety, compliance and commercial tooling in one platform.",
    payoff: "A documented, production-tested operating standard for compliant, resilient transport operations. Thanks for taking the tour!",
  },
];

const Row = ({ label, text, color }) => text ? (
  <div className="flex gap-2 text-sm">
    <span className="label-mini font-semibold flex-shrink-0 w-[88px]" style={{ color }}>{label}</span>
    <span className="text-foreground/80">{text}</span>
  </div>
) : null;

export const GuidedTour = ({ open, setTab, onClose }) => {
  const [i, setI] = useState(0);
  const step = STEPS[i];

  // Drive admin navigation as the step changes.
  useEffect(() => {
    if (!open) return;
    if (step?.target) setTab(step.target);
  }, [open, i, step, setTab]);

  const next = useCallback(() => setI((n) => Math.min(n + 1, STEPS.length - 1)), []);
  const prev = useCallback(() => setI((n) => Math.max(n - 1, 0)), []);
  const close = useCallback(() => { setI(0); onClose(); }, [onClose]);
  const copy = useCallback((text) => {
    try { navigator.clipboard?.writeText(text)?.catch(() => {}); } catch (_) { /* clipboard unavailable */ }
    toast.success("Copied");
  }, []);

  if (!open) return null;
  const Icon = step.icon;
  const isLast = i === STEPS.length - 1;
  const pct = Math.round(((i + 1) / STEPS.length) * 100);

  return (
    <div className="fixed inset-x-0 bottom-0 z-[80] p-3 sm:p-5 pointer-events-none" data-testid="guided-tour">
      <div className="max-w-2xl mx-auto bg-card border border-border rounded-xl shadow-2xl pointer-events-auto overflow-hidden">
        <div className="h-1 bg-muted">
          <div className="h-full bg-[#6D28D9] transition-all" style={{ width: `${pct}%` }} />
        </div>
        <div className="p-4 sm:p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 grid place-items-center bg-[#6D28D9] text-white rounded-md flex-shrink-0">
                <Icon className="w-5 h-5" />
              </div>
              <div>
                <div className="label-mini text-muted-foreground" data-testid="tour-step-counter">Step {i + 1} of {STEPS.length}</div>
                <h3 className="font-heading text-lg font-black tracking-tight leading-tight">{step.title}</h3>
              </div>
            </div>
            <button onClick={close} className="text-muted-foreground hover:text-foreground" data-testid="tour-close" aria-label="Close tour">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="mt-3.5 space-y-2" data-testid="tour-step-body">
            <Row label="What it does" text={step.what} color="#0b1f3a" />
            <Row label="Why it matters" text={step.why} color="#6D28D9" />
            <Row label="Problem solved" text={step.problem} color="#B45309" />
            <Row label="The payoff" text={step.payoff} color="#15803D" />
          </div>

          {step.credentials && (
            <div className="mt-3 p-3 rounded-md bg-muted/50 border border-border" data-testid="tour-role-credentials">
              <div className="label-mini text-muted-foreground mb-1.5">{step.credentials.label}</div>
              <div className="flex items-center justify-between gap-2">
                <code className="font-mono-tab text-sm truncate">{step.credentials.email}</code>
                <button
                  onClick={() => copy(step.credentials.email)}
                  className="text-muted-foreground hover:text-foreground flex-shrink-0"
                  title="Copy email" data-testid="tour-copy-credential"
                >
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="text-xs text-muted-foreground mt-1">Password: <code className="font-mono-tab">demo123</code></div>
              <div className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
                Open a <strong>new private / incognito window</strong> and sign in with this {step.credentials.role} login —
                that keeps the {step.credentials.role} session separate from this Admin Control Room.
                (One browser window shares a single login, so the {step.credentials.role} app can't open in this same session.)
              </div>
            </div>
          )}

          <div className="mt-4 flex items-center justify-between gap-2">
            <button onClick={close} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1.5" data-testid="tour-explore-freely">
              <Compass className="w-3.5 h-3.5" /> Explore freely
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={prev} disabled={i === 0}
                className="px-3 py-1.5 text-sm rounded-md border border-border disabled:opacity-40 flex items-center gap-1.5"
                data-testid="tour-prev"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Back
              </button>
              {isLast ? (
                <button onClick={close} className="px-3.5 py-1.5 text-sm rounded-md bg-[#6D28D9] text-white font-semibold" data-testid="tour-finish">
                  Finish tour
                </button>
              ) : (
                <button onClick={next} className="px-3.5 py-1.5 text-sm rounded-md bg-[#6D28D9] text-white font-semibold flex items-center gap-1.5" data-testid="tour-next">
                  Next <ArrowRight className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuidedTour;
